/**
 * 강사 통계 페이지 — 학생을 고르지 않았을 때 오른쪽 칸(설계 §3-3 · §3-5 · D6). 본인이 오늘 담임인 반만.
 *
 * 통계 API(ST*)는 부르지 않는다 — 서버가 강사에게 403 이다(요청서 §10). 목록(S1, 서버가 범위 학생만 줌) ·
 * 반(C1, 담임 = 본인) · 내 일정(E9)으로 화면에서 센다(academyModel.teacherKpis).
 *
 * 숫자 띠 4(내 반 재원 · 30일 안 퇴원 예정 · 이번 주 수업 · 담임 부재 예정) + 탭.
 * P1 은 「내 반」 탭 하나다 — 캘린더 · 메모 탭은 P2 에서 더한다(설계 §11-1).
 * 반 카드에는 지난 수업의 출결 입력 여부와 [출결 입력](본인 반 회차, 요청서 §10 판 4.1). 맨 아래 [내 반 정보]는 반 화면으로.
 *
 * 아직 서버에 없는 것은 부르지 않는다(stageGate, 백엔드 8단계 전달 §4-B · §4-C):
 *   직원 일정(E9) — 「담임 부재 예정」은 「준비 중」, 반 카드의 부재 표시는 없다. 목록 · 반과 한 Promise.all 에 묶으면
 *     그 404 하나로 「내 반 현황」 전체가 막히고 [내 반 정보] 단추도 사라진다.
 *   출결 회차(AT1) — 반 카드의 「지난 수업」 줄을 그리지 않는다.
 */
import React, { useState } from 'react';
import { AcademyButton } from './controls';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../common';
import { listAllStudents, listClasses, listClassSessions, listStaffSchedules } from './academyApi';
import { membersOf, teacherKpis, upcomingClassDays, watchList } from './academyModel';
import { addDays, formatMonthDay, formatSchedule, todayKST, weekdayLabel } from './periodDates';
import { lockedLabel, lockedStage } from './stageGate';
import { Box, ErrorNotice, KpiCard, NameLink, SegTabs, Spinner, ToneBadge, useAcademyVersion, useAsync, useElementWidth } from './ui';
import AttendanceModal from './modals/AttendanceModal';

const TABS = [['my', '내 반']];
const TWO_COL_MIN = 700;
const FOUR_KPI_MIN = 520;

function ClassCard({ cls, students, schedules, sessions, today, onInput, absenceLocked = false }) {
  const count = membersOf(students, cls.class_id).length;
  const days = upcomingClassDays(cls, schedules, today, 7);
  // 지난 수업 — 오늘까지의 가장 최근 회차(휴강 제외)
  const last = [...(sessions || [])].reverse().find((s) => s.date <= today && s.status !== 'canceled');
  const recorded = last && last.status === 'held' && last.counts.unrecorded === 0;
  return (
    <Box title={cls.name} sub={formatSchedule(cls.slots) || '시간표 없음'}>
      <p className="text-xs text-gray-500">
        재원 {count}명{cls.teacher_start_on ? ` · 담임 ${formatMonthDay(cls.teacher_start_on)}부터` : ''}
      </p>
      <ul className="mt-2 divide-y divide-gray-100">
        {last && (
          <li className="flex items-center gap-2 py-1.5 text-xs">
            <span className="w-10 shrink-0 font-mono text-gray-500">{formatMonthDay(last.date)}</span>
            <span className="text-gray-900">지난 수업</span>
            <ToneBadge tone={recorded ? 'ok' : 'warn'}>{recorded ? '출결 입력됨' : '출결 미입력'}</ToneBadge>
            {!recorded && <AcademyButton type="button" onClick={() => onInput(last)} className="ml-auto">출결 입력</AcademyButton>}
          </li>
        )}
        {days.length === 0 && <li className="py-1.5 text-xs text-gray-500">7일 안에 수업이 없습니다.</li>}
        {days.map((d) => (
          <li key={d.date} className="flex items-center gap-2 py-1.5 text-xs">
            <span className="w-10 shrink-0 font-mono text-gray-500">{formatMonthDay(d.date)}</span>
            <span className="text-gray-900">{weekdayLabel(d.weekday)} 수업</span>
            {d.absent && <ToneBadge tone="danger">담임 부재 · {d.absent}</ToneBadge>}
          </li>
        ))}
        {/* 직원 일정(E9)이 서버에 없는 동안 부재 표시는 「준비 중」(백엔드 8단계 전달 §4-B) */}
        {absenceLocked && <li className="py-1.5 text-xs text-gray-400">담임 부재 표시 · {lockedLabel()}</li>}
      </ul>
    </Box>
  );
}

function TeacherStatsPanel({ me, meLoading, meError, onRetryMe, tab = 'my', onTabChange, onOpenStudent, onOpenClasses, onBack }) {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const [input, setInput] = useState(null);
  const today = todayKST();
  const [ref, width] = useElementWidth();
  const staffId = me?.staff_id ?? null;
  const data = useAsync((signal) => {
    if (staffId == null) return Promise.resolve(null);
    return Promise.all([
      listAllStudents({ state: ['enrolled', 'paused'], signal }, getAccessToken),
      listClasses({ state: 'open', teacherStaffId: staffId, signal }, getAccessToken),
    ]).then(async ([students, classes]) => {
      // 반마다 지난 일주일 회차 — 「지난 수업 출결 입력 여부」(요청서 AT1). 출결이 열리기 전에는 부르지 않는다
      const sessions = lockedStage('attendance') ? classes.map(() => [])
        : await Promise.all(classes.map((c) => listClassSessions(c.class_id, { from: addDays(today, -7), to: today, signal }, getAccessToken).catch(() => [])));
      return { students: students.items, classes, sessions: Object.fromEntries(classes.map((c, i) => [c.class_id, sessions[i]])) };
    });
  }, `teacher-${staffId}-${today}-${version}`);
  // 직원 일정(E9)은 따로 받는다 — 실패해도 「담임 부재 예정」만 비고 나머지는 선다. 열리기 전에는 부르지 않는다(§4-B)
  const scheduleLocked = !!lockedStage('staffSchedule');
  const schedulesRes = useAsync((signal) => (staffId == null || scheduleLocked ? Promise.resolve([])
    : listStaffSchedules({ from: today, to: addDays(today, 30), staffId, signal }, getAccessToken)), `teacher-schedules-${staffId}-${today}-${version}`);

  const d = data.data ? { ...data.data, schedules: schedulesRes.data || [] } : null;
  const k = d ? teacherKpis({ ...d, today }) : null;
  const loading = meLoading || (data.loading && !d);
  const error = meError || data.error;
  const unlinked = !meLoading && !meError && !me;
  const firstAbsence = k?.absences[0];

  return (
    <div ref={ref} className="flex size-full flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
      <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3">
        {onBack && (
          <Button variant="ghost" size="sm" onClick={onBack} aria-label="목록으로" className="flex shrink-0 items-center gap-1">
            <ArrowLeft className="size-4" />
          </Button>
        )}
        <h3 className="shrink-0 text-base font-semibold text-gray-900">내 반 현황</h3>
        <span className="truncate text-xs text-gray-500">
          {me?.name || '강사'}{d ? ` · 담임 반 ${d.classes.length}개` : ''} · 오늘 기준
        </span>
        <span className="ml-auto hidden shrink-0 text-xs text-gray-400 sm:inline">학원 전체 통계는 매니저만</span>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {unlinked && (
          <p role="status" className="py-12 text-center text-sm text-gray-500">
            이 계정에 연결된 직원 기록이 없어 내 반을 찾지 못했습니다.
            <span className="mt-1 block text-xs text-gray-400">매니저에게 직원 연결을 요청해 주세요.</span>
          </p>
        )}
        {error && <ErrorNotice error={error} onRetry={meError ? onRetryMe : data.reload} />}
        {!unlinked && !error && (
          <>
            <div className={`grid gap-2 ${width >= FOUR_KPI_MIN ? 'grid-cols-4' : 'grid-cols-2'}`}>
              {/* 재원은 휴원을 뺀 수(오너 2026-10-10) */}
              <KpiCard loading={loading} label="내 반 재원" value={k ? k.myStudents - k.paused : null} sub={`중복 없이 · 휴원 ${k?.paused ?? 0}명 따로`} />
              <KpiCard loading={loading} warn label="30일 안 퇴원 예정" value={k?.withdrawSoon} sub={k?.withdrawSoonFirst ? `${formatMonthDay(k.withdrawSoonFirst.date)} ${k.withdrawSoonFirst.name}` : '없음'} />
              <KpiCard loading={loading} label="이번 주 수업" value={k ? `${k.weekSessions}회` : null} sub={k ? k.perClass.map((c) => `${c.name} ${c.count}`).join(' · ') : ''} />
              {scheduleLocked
                ? <KpiCard loading={loading} label="담임 부재 예정" value={lockedLabel()} sub="" />
                : <KpiCard loading={loading} label="담임 부재 예정" value={k?.absences.length} sub={firstAbsence ? `${formatMonthDay(firstAbsence.date)} ${firstAbsence.kind} · ${firstAbsence.className}` : '30일 안 없음'} />}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <SegTabs tabs={TABS} value="my" onChange={onTabChange} ariaLabel="내 반 탭" />
              <span className="ml-auto text-xs text-gray-500">이번 주 {formatMonthDay(today)}~{formatMonthDay(addDays(today, 6))}</span>
            </div>
            {loading && <Spinner label="내 반 불러오는 중" />}
            {d && (
              <>
                {d.classes.length === 0 ? (
                  <p className="py-6 text-center text-sm text-gray-500">오늘 담임인 반이 없습니다.</p>
                ) : (
                  <div className={`grid gap-3 ${width >= TWO_COL_MIN ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {d.classes.map((c) => <ClassCard key={c.class_id} cls={c} students={d.students} schedules={d.schedules} sessions={d.sessions[c.class_id]} today={today} absenceLocked={scheduleLocked} onInput={(s) => setInput({ classId: c.class_id, className: c.name, date: s.date, startTime: s.start_time })} />)}
                  </div>
                )}
                <Box title="살펴볼 학생">
                  {watchList(d.students).length === 0 ? (
                    <p className="py-1 text-xs text-gray-500">살펴볼 학생이 없습니다.</p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {watchList(d.students).map(({ student: s, reasons }) => (
                        <li key={s.student_id} className="flex flex-wrap items-center gap-2 py-1.5 text-xs">
                          {reasons.map((r) => <ToneBadge key={r.text} tone={r.tone}>{r.text}</ToneBadge>)}
                          <NameLink id={s.student_id} name={s.student_name} onOpen={onOpenStudent} />
                          <span className="text-gray-500">{s.classes.map((c) => c.class_name).join(' · ') || '반 없음'}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Box>
              </>
            )}
          </>
        )}
        {/* 내 반 정보 — 누르면 이 칸이 반 화면으로 바뀐다(오너 2026-10-10, 강사는 읽기 · 출결 · 반 메모만) */}
        {!unlinked && !error && onOpenClasses && (
          <div className="flex justify-center border-t border-gray-100 pt-3">
            <AcademyButton type="button" onClick={onOpenClasses} >
              <BookOpen className="size-4" />내 반 정보 — 시간표 · 학생 · 출결 · 반 메모
            </AcademyButton>
          </div>
        )}
      </div>
      {input && <AttendanceModal {...input} onClose={() => setInput(null)} />}
    </div>
  );
}

export default TeacherStatsPanel;
